/**
 * @file        scheduler-constants.js
 *              Contants and constant-like precomputed values for use with/by DCPv4.
 *              All values in this module are completely deterministic and will change
 *              only if the source code changes.
 * @author      Wes Garland, wes@kingsds.network
 * @date        Nov 2020
 */
'use strict';

/** Pre-defined, hard-coded compute groups */
exports.computeGroups = {
  public: {
    opaqueId: 'WHhetL7mj1w1mw1XV6dxyC', 
    id: 1,
    name: 'Public Compute Group (open access)',
    joinKey: 'public',
    joinHash: '',
  },
};

/** Currently used bit masks for flags column of jobs table. There is capacity for 31 bit masks. */
exports.jobFlags = {
  localExec:              1 << 0,   /* local exec job; prevent from joining compute groups */
  open:                   1 << 1,   /* job still open, i.e. able to add more slices */
  workerConsole:          1 << 2,   /* job is allowed to log to worker's console - if worker permits also */
  greedyEstimation:       1 << 3,   /* Allows job in estimation to have requestTask return multiple estimation slices. */
  discrete:               1 << 4,   /* job is in discrete mode - only return 1 slice per fetch */
  useStrict:              1 << 5,   /* run work function in strict mode */
};

exports.capabilities = {
  engine: {
    v8:                   1 << 0,
    spidermonkey:         1 << 1,
    browser:              1 << 2,
  },
  environment: {
    offscreenCanvas:      1 << 3,
    fdlibm:               1 << 4,
    webgpu:               1 << 5,
  },
  KVINv9:                 1 << 6,
};

exports.workerIdLength = 22;
exports.workerIdAlphabet = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';

/**
 *  In dcpConfig.scheduler hive, storage_byJobId, storage_byJobName and storage_byJobOwner, when
 *  baseUrl===false, normal storage in storeJobValue is skipped and exports.skipStorage is placed in
 *  the specified database row(s). In https://dcp/dev/null, dcp is lower case because
 *  (new URL('https://STR')).href === 'https://str' and hence the same is true for DcpURL.
 */
exports.skipStorage = 'https://dcp/dev/null';

/** 
 * Constant Groups have a load-time side effect of needing dcpConfig already loaded, in order to
 * determine how they are constructed; proxies are relatively heavy and we don't want them running
 * on production builds if possible.
 */
function initConstantGroups(build)
{
  clearTimeout(initConstantGroups.timerHnd);

  /**
   * The list of all possible job status in the status column of the jobs table.
   */
  exports.jobStatus = new SchedulerConstantGroup(
    'cancelled',
    'corrupted',
    'estimation',
    'finished',
    'running',
    'paused',
    'new',
  );

  exports.jobValueKind = new SchedulerConstantGroup(
    'results',
    'sliceData',
    'jobArguments',
    'workFunctions',
  );

  /**
   * The list of all possible slice status in the status column of the
   * activeSlices table.
   */
  exports.sliceStatus = new SchedulerConstantGroup(
    'overdue',
    'tiebreaker',
    'scheduled',
    'working',
    'paused', /** XXXpfr @todo Unimplemented, but on future roadmap for supervisor pausing. */
    'returned',
    'new',
  );

  function SchedulerConstantGroup()
  {
    var argv = Array.from(arguments);
    
    for (let el of argv)
      this[el] = el;

    if (build === 'debug')
    {
      let px = new Proxy(this, {
        get: (target, prop) => {
          if (!target.hasOwnProperty(prop))
            throw new Error(`no such constant: '${prop}'`);
          return target[prop];
        },
        set: (target, prop) => {
          throw new Error('constants are immutable!');
        },
        deleteProperty: (_prop) => {
          throw new Error('constant groups are immutable!');
        },
      });

      return px;
    }
  }

  /** Temporary arrays for backwards compatibility - do not use for new code */
  exports.sliceStatuses = Object.keys(exports.sliceStatus);
  exports.jobStatuses   = Object.keys(exports.jobStatus);
}

initConstantGroups('release');

(globalThis.setImmediate || setTimeout)(function initConstantGroupsInDebugMode() {
  try
  {
    /* Init the consts groups in debug mode only once dcpConfig is loaded. We can use "node-only code" 
     * here, because we know that the browser initializes dcpConfig before the bundle is evaluated.
     *
     * We run one tick down the event loop because of dcp-client init semantics.
     */
    if (typeof dcpConfig !== 'undefined' && dcpConfig.build)
      initConstantGroups(dcpConfig.build);
    else
    {
      const { requireNative } = require('dcp/dcp-client/webpack-native-bridge');
      requireNative('dcp/node-libs/config').on('loaded', () => initConstantGroups(dcpConfig.build));

      initConstantGroups.timerHnd = setTimeout(() => {
        if (typeof dcpConfig !== 'undefined' && dcpConfig.build)
          initConstantGroups(dcpConfig.build);
        else
          console.warn('scheduler-constants not fully initialized; please load dcpConfig!');
      }, 10000);
      initConstantGroups.timerHnd.unref();
    }
  }
  catch(error)
  {
    if (false
        || (process && process.env && process.env.DCP_DEBUG)
        || (dcpConfig && dcpConfig.build === 'debug'))
      console.error('Cannot put scheduler constants into debug mode:', error);
  }
});
